/**
 * `@vello/brand` — the Vello brand kit, versioned as a Vello-owned package (design-system protocol:
 * "import and version the authoritative Vello design artifacts into Vello-owned packages before broad UI
 * delivery"). This is the **single source** for the wordmark, symbol (mark), app icon, and animated splash.
 *
 * The raw approved artifacts live under `./assets/` (SVG for the vector marks, PNG renders at fixed sizes, a
 * Lottie JSON for the animated symbol). Import them as URLs from a bundler, e.g.
 *   `import wordmark from '@vello/brand/assets/wordmark/svg/vello-wordmark-currentcolor.svg'`.
 * Prefer the `currentcolor` SVG variants so the mark inherits a `@vello/tokens` color role and stays
 * theme-correct; never recompose the wordmark in a live font and never recolor it with a raw hex in an app —
 * pick the variant that already carries the intended color, or the `currentcolor` variant plus a token.
 *
 * Whenever UI needs an icon, wordmark, symbol, or splash, it comes from here — not an ad-hoc SVG, not a
 * MotoXpress donor artifact. This module exports a typed manifest of what the kit ships so consumers discover
 * the approved variants instead of guessing file names.
 */
/** The brand's committed color decisions (documented; apps consume them through `@vello/tokens` roles). */
export const BRAND_COLORS = {
    /** Primary brand color — "azul-noite" (night blue). The one brand color the marks are built on. */
    primary: '#233048',
    /** Legacy ink used by older mark renders. */
    ink: '#0B0F1A',
    /** Semantic only (status + map pins) — never a brand accent. */
    semantic: { danger: '#F0122E', success: '#10C554' },
};
const asset = (label, path) => ({ label, path: `@vello/brand/assets/${path}` });
/**
 * The approved artifacts, grouped by kind. `currentcolor` variants inherit the surrounding text color (use
 * with a token); `branco` = white (for dark surfaces); `navy` = the primary color (for light surfaces); `ink`
 * = legacy. App-icon renders are square (navy field), sized for favicons and PWA icons.
 */
export const BRAND_KIT = {
    wordmark: {
        currentcolor: asset('Wordmark (inherits color)', 'wordmark/svg/vello-wordmark-currentcolor.svg'),
        white: asset('Wordmark on dark', 'wordmark/svg/vello-wordmark-branco.svg'),
        navy: asset('Wordmark on light', 'wordmark/svg/vello-wordmark-navy.svg'),
        ink: asset('Wordmark (legacy ink)', 'wordmark/svg/vello-wordmark-ink.svg'),
    },
    symbol: {
        currentcolor: asset('Symbol (inherits color)', 'simbolo/svg/vello-mark-mono-currentcolor.svg'),
        white: asset('Symbol on dark', 'simbolo/svg/vello-mark-branco.svg'),
        navy: asset('Symbol on light', 'simbolo/svg/vello-mark-navy.svg'),
        onLight: asset('Symbol (legacy on-light)', 'simbolo/svg/vello-mark-on-light.svg'),
    },
    appIcon: {
        /** Square navy app-icon renders, keyed by pixel size (favicon + PWA + apple-touch). */
        sizes: [16, 32, 48, 64, 120, 152, 167, 180, 192, 512],
        at: (size) => asset(`App icon ${size}px`, `app-icon/sizes/vello-appicon-navy-${size}.png`),
        /**
         * The three approved 1024 px icons. D676 (owner, 2026-10-22): the two native apps differ — the customer app ("Vello")
         * keeps `navy`, the provider app ("Vello Prestador") uses `light` (white background).
         */
        full: {
            navy: asset('App icon (navy)', 'app-icon/vello-appicon-navy.png'),
            light: asset('App icon (white background)', 'app-icon/vello-appicon-light.png'),
            dark: asset('App icon (dark)', 'app-icon/vello-appicon-dark.png'),
        },
    },
    /** The animated symbol (RFC-6238-style glow), 1:1 with the static mark, for splash/loading on dark. */
    animatedSplash: asset('Animated splash (Lottie)', 'animated-splash-simbolo/vello-splash.json'),
};
export * from './map-vehicles.js';
// D697: the vehicle PNGs are bundler assets, so their handles live in the `@vello/brand/map-vehicle-images` subpath.
// D800: the Design System's map markers (embarque, destino, paradas) — the map lollipops and the bare heads of list rows.
export * from './map-markers.js';
// D856 (owner D845 Q5): the one name of each console.
export * from './console-names.js';
//# sourceMappingURL=index.js.map